/**
 * Time utility functions for the TimePicker component
 */

type Period = 'AM' | 'PM'

/**
 * Format a 24-hour time string to 12-hour format
 * @param time24 - Time in HH:mm format (e.g., "14:30")
 * @returns Time in h:mm AM/PM format (e.g., "2:30 PM")
 */
export function formatTime12Hour(time24: string): string {
  if (!time24) return ''

  const [hoursStr, minutesStr] = time24.split(':')
  let hours = parseInt(hoursStr, 10)
  const minutes = minutesStr || '00'

  const period: Period = hours >= 12 ? 'PM' : 'AM'

  if (hours === 0) {
    hours = 12
  } else if (hours > 12) {
    hours = hours - 12
  }

  return `${hours}:${minutes} ${period}`
}

/**
 * Parse a 12-hour time string to 24-hour format
 * @param time12 - Time in h:mm AM/PM format (e.g., "2:30 PM")
 * @returns Time in HH:mm format (e.g., "14:30")
 */
export function parseTime12Hour(time12: string): string {
  if (!time12) return ''

  const match = time12.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
  if (!match) return ''

  let hours = parseInt(match[1], 10)
  const minutes = match[2]
  const period = match[3].toUpperCase() as Period

  if (period === 'AM' && hours === 12) {
    hours = 0
  } else if (period === 'PM' && hours !== 12) {
    hours = hours + 12
  }

  return `${hours.toString().padStart(2, '0')}:${minutes}`
}

/**
 * Get the period (AM/PM) from a 24-hour time string
 * @param time24 - Time in HH:mm format
 * @returns 'AM' or 'PM'
 */
export function getPeriodFromTime(time24: string): Period {
  if (!time24) return 'AM'

  const [hoursStr] = time24.split(':')
  const hours = parseInt(hoursStr, 10)

  return hours >= 12 ? 'PM' : 'AM'
}

/**
 * Generate time options for a given period
 * @param interval - Minutes between options (default: 15)
 * @param period - 'AM' or 'PM'
 * @returns Array of time strings in HH:mm format
 */
export function generateTimeOptions(
  interval: number = 15,
  period: Period = 'AM'
): string[] {
  const options: string[] = []

  // AM: 12:00 AM to 11:45 AM (0:00 to 11:45 in 24h)
  // PM: 12:00 PM to 11:45 PM (12:00 to 23:45 in 24h)
  const startHour = period === 'AM' ? 0 : 12
  const endHour = period === 'AM' ? 11 : 23

  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      options.push(time24)
    }
  }

  return options
}

/**
 * Generate all time options for a full day
 * @param interval - Minutes between options (default: 15)
 * @returns Array of time strings in HH:mm format
 */
export function generateAllTimeOptions(interval: number = 15): string[] {
  const options: string[] = []

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      options.push(time24)
    }
  }

  return options
}

/**
 * Check if a time is within a given range
 * @param time - Time in HH:mm format
 * @param minTime - Minimum time in HH:mm format
 * @param maxTime - Maximum time in HH:mm format
 * @returns true if time is within range
 */
export function isTimeInRange(
  time: string,
  minTime?: string,
  maxTime?: string
): boolean {
  if (!minTime && !maxTime) return true

  const timeMinutes = timeToMinutes(time)

  if (minTime && timeMinutes < timeToMinutes(minTime)) {
    return false
  }

  if (maxTime && timeMinutes > timeToMinutes(maxTime)) {
    return false
  }

  return true
}

/**
 * Convert time string to minutes since midnight
 * @param time - Time in HH:mm format
 * @returns Minutes since midnight
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}
